import { useEffect, useState } from "react";
import api from "@/lib/api";
import { authFileUrl as fileUrl } from "@/lib/fileurl";
import { X, Printer, FileDown } from "lucide-react";

export default function ReceiptModal({ saleId, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!saleId) return;
    api.get(`/receipts/${saleId}`).then((r) => setData(r.data)).catch(() => setErr("No se pudo cargar el recibo."));
  }, [saleId]);

  if (!saleId) return null;
  const sale = data?.sale;
  const b = data?.branding || {};
  const logo = b.logo_url ? fileUrl(b.logo_url) : "";
  const fmt = (n) => `$${(n || 0).toFixed(2)}`;
  const printPdf = () => window.open(fileUrl(`/api/receipts/${saleId}/pdf`), "_blank");

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center bg-black/70 p-4 overflow-y-auto print:bg-white print:p-0" data-testid="receipt-modal">
      <div className="bg-white text-black w-full max-w-sm my-8 print:my-0 print:max-w-full print:shadow-none shadow-2xl">
        <div className="flex items-center justify-between px-4 py-3 border-b border-black/10 print:hidden">
          <span className="font-bold text-sm uppercase tracking-wide">Recibo</span>
          <div className="flex items-center gap-2">
            <button data-testid="receipt-print-button" onClick={() => window.print()} className="flex items-center gap-1.5 bg-black text-white text-xs px-3 py-1.5 hover:bg-black/80"><Printer className="w-3.5 h-3.5" /> Imprimir</button>
            <button data-testid="receipt-pdf-button" onClick={printPdf} className="flex items-center gap-1.5 border border-black/20 text-xs px-3 py-1.5 hover:bg-black/5"><FileDown className="w-3.5 h-3.5" /> PDF</button>
            <button data-testid="receipt-close-button" onClick={onClose} className="p-1.5 hover:bg-black/5"><X className="w-4 h-4" /></button>
          </div>
        </div>

        {err && <p className="p-6 text-center text-red-600 text-sm">{err}</p>}
        {!data && !err && <p className="p-6 text-center text-black/40 text-sm">Cargando...</p>}

        {sale && (
          <div id="receipt-printable" className="px-6 py-5 font-mono text-[13px] leading-relaxed">
            <div className="text-center mb-3">
              {logo && <img src={logo} alt="logo" className="mx-auto mb-2 max-h-16 object-contain" data-testid="receipt-logo" />}
              <p className="font-bold text-base">{b.brand_name || "GLG GYM"}</p>              {b.address && <p className="text-[11px]">{b.address}</p>}
              {b.phone && <p className="text-[11px]">{b.phone}</p>}
              {b.email && <p className="text-[11px]">{b.email}</p>}
            </div>
            <div className="border-t border-dashed border-black/40 my-2" />
            {sale.document_type === "invoice" ? (
              <>
                <p className="text-center font-bold text-sm">FACTURA</p>
                {b.rtn_emisor && <p>RTN Emisor: {b.rtn_emisor}</p>}
                <p>CAI: <span className="break-all">{sale.cai || "—"}</span></p>
                <p>Factura N°: <b>{sale.invoice_number || "—"}</b></p>
                {sale.rango_autorizado && <p className="text-[11px]">Rango autorizado: {sale.rango_autorizado}</p>}
                {sale.fecha_limite_emision && <p className="text-[11px]">Fecha límite emisión: {new Date(sale.fecha_limite_emision).toLocaleDateString()}</p>}
                <div className="border-t border-dashed border-black/40 my-1" />
                <p>Cliente: {sale.client_name || sale.member_name || "—"}</p>
                <p>RTN Cliente: {sale.client_rtn || "Consumidor final"}</p>
              </>
            ) : (
              <>
                <p>Recibo: <b>{sale.receipt_number || "—"}</b></p>
                <p>Cliente: {sale.member_name || "—"}</p>
              </>
            )}
            <p>Fecha: {new Date(sale.date).toLocaleString()}</p>
            <p>Cajero: {sale.cashier || "—"}</p>
            <p>Pago: <span className="capitalize">{sale.payment_method}</span></p>
            <div className="border-t border-dashed border-black/40 my-2" />
            {sale.type === "plan" ? (
              <div className="flex justify-between"><span>{sale.plan_name || "Membresía"}</span><span>{fmt(sale.total)}</span></div>
            ) : (
              (sale.items || []).map((it, i) => (
                <div key={`${it.name}-${i}`} className="flex justify-between"><span>{it.qty}x {it.name}</span><span>{fmt(it.subtotal)}</span></div>
              ))
            )}
            <div className="border-t border-dashed border-black/40 my-2" />
            <div className="flex justify-between font-bold text-base"><span>TOTAL</span><span>{fmt(sale.total)}</span></div>
            <p className="text-center text-[11px] italic mt-4">{b.receipt_footer || "Gracias por su preferencia."}</p>
            <div className="border-t border-dashed border-black/40 my-3" />
            <div className="text-center">
              <p className="text-[9px] uppercase tracking-widest text-black/40">Generado por</p>
              <img src="/glg-logo-mark.png" alt="GLG GYM CONTROL" className="h-9 mx-auto my-1 object-contain" data-testid="receipt-platform-logo" />
              <p className="text-[10px] font-bold tracking-wide">GLG GYM CONTROL</p>
            </div>
          </div>
        )}
      </div>
      <style>{`@media print { body * { visibility: hidden; } #receipt-printable, #receipt-printable * { visibility: visible; } #receipt-printable { position: absolute; left: 0; top: 0; width: 100%; } }`}</style>
    </div>
  );
}
